<template>
  <div class="p-lg pb-leftSquiggleHeight">
    <h1>File Picker</h1>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>No need to set a size - File Picker will fill its container</h2>
      <div class="flex gap-base w-full flex-wrap">
        <div class="w-menuWidth h-menuWidth">
          <FilePicker
            acceptedFileTypes=".pdf,.docx,.doc,.xlsx,.xls"
            uploaderMessage="Try us on mobile size"
          />
        </div>
        <div class="w-menuWidth h-envelopeCardRight">
          <FilePicker
            acceptedFileTypes=".pdf,.docx,.doc,.xlsx,.xls"
            uploaderMessage="Try us on mobile size"
          />
        </div>
        <div class="w-brandingPreview h-appHeaderRightSquiggleW">
          <FilePicker
            acceptedFileTypes=".pdf,.docx,.doc,.xlsx,.xls"
            uploaderMessage="Try us on mobile size"
          />
        </div>
      </div>
    </div>
    <div class="p-base mb-xl bg-signableBrandColours-white">
      <h2>Allows multiple file uploads by default, but you can set it to only allow files added one at a time</h2>
      <div class="flex gap-base">
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            acceptedFileTypes=""
            uploaderMessage="I can take multiple files simultaneously"
            @add="(files) => handleAdd(files, oneAtATimeFileList)"
            @invalidFiles="handleError"
          />
        </div>
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            :multiple="false"
            acceptedFileTypes=""
            uploaderMessage="I only take files one at a time"
            @add="(files) => handleAdd(files, oneAtATimeFileList)"
            @invalidFiles="handleError"
          />
        </div>
      </div>
      <div class="items-center w-fit flex flex-wrap gap-base mt-base bg-signableBrandColours-pink rounded p-base">
        <span class="p-base text-white">Files:</span>
        <DismissibleInfoPill
          v-for="file in oneAtATimeFileList"
          @dismiss="(id) => handleRemove(id, oneAtATimeFileList)"
          :key="file.id"
          :text="file.name"
          :id="file.id"
          ec="file_picker_design_page"
        />
      </div>
    </div>
    <div class="flex gap-base flex-wrap">
      <div class="p-base mb-xl bg-signableBrandColours-white w-fit">
        <h2>Accepts .pdf only</h2>
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            acceptedFileTypes=".pdf"
            :uploaderMessage="$t('functional.uploaderMessage')"
            @add="(files) => handleAdd(files, pdfFileList)"
            @invalidFiles="handleError"
          />
        </div>
        <div class="items-center flex flex-col gap-base mt-base bg-signableBrandColours-orange rounded p-base">
          <span class="p-base text-white">PDFs uploaded:</span>
          <DismissibleInfoPill
            v-for="file in pdfFileList"
            @dismiss="(id) => handleRemove(id, pdfFileList)"
            :key="file.id"
            :text="file.name"
            :id="file.id"
            ec="file_picker_design_page"
          />
        </div>
      </div>
      <div class="p-base mb-xl bg-signableBrandColours-white">
        <h2>Accepts .svg only</h2>
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            acceptedFileTypes=".svg"
            :uploaderMessage="$t('functional.uploaderMessage')"
            @add="(files) => handleAdd(files, svgFileList)"
            @invalidFiles="handleError"
          />
        </div>
        <div class="items-center flex flex-col gap-base mt-base bg-signableBrandColours-orange rounded p-base">
          <span class="p-base text-white">SVGs uploaded:</span>
          <DismissibleInfoPill
            v-for="file in svgFileList"
            @dismiss="(id) => handleRemove(id, svgFileList)"
            :key="file.id"
            :text="file.name"
            :id="file.id"
            ec="file_picker_design_page"
          />
        </div>
      </div>
      <div class="p-base mb-xl bg-signableBrandColours-white">
        <h2>Accepts anything</h2>
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            acceptedFileTypes=""
            :uploaderMessage="$t('functional.uploaderMessage')"
            @add="(files) => handleAdd(files, anythingFileList)"
            @invalidFiles="handleError"
          />
        </div>
        <div class="items-center flex flex-col gap-base mt-base bg-signableBrandColours-orange rounded p-base">
          <span class="p-base text-white">Anythings uploaded:</span>
          <DismissibleInfoPill
            v-for="file in anythingFileList"
            @dismiss="(id) => handleRemove(id, anythingFileList)"
            :key="file.id"
            :text="file.name"
            :id="file.id"
            ec="file_picker_design_page"
          />
        </div>
      </div>
      <div class="p-base mb-xl bg-signableBrandColours-white">
        <h2>.pdf, .docx, .doc, .xlsx & .xls</h2>
        <div class="h-widgetSkeletonH w-widgetSkeletonH">
          <FilePicker
            acceptedFileTypes=".pdf,.docx,.doc,.xlsx,.xls"
            :uploaderMessage="$t('functional.uploaderMessage')"
            @add="(files) => handleAdd(files, signableFileList)"
            @invalidFiles="handleError"
          />
        </div>
        <div class="items-center flex flex-col gap-base mt-base bg-signableBrandColours-orange rounded p-base">
          <span class="p-base text-white">Signable compatible files uploaded:</span>
          <DismissibleInfoPill
            v-for="file in signableFileList"
            @dismiss="(id) => handleRemove(id, signableFileList)"
            :key="file.id"
            :text="file.name"
            :id="file.id"
            ec="file_picker_design_page"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

import { FilePicker } from '@/components/library/filePickers/FilePicker';
import { DismissibleInfoPill } from '@/components/library/pills/DismissibleInfoPill';
import { EventBus } from '@/utils/event-bus';

const pdfFileList = ref([]);
const svgFileList = ref([]);
const anythingFileList = ref([]);
const signableFileList = ref([]);
const oneAtATimeFileList = ref([]);
function handleAdd(files, list) {
  Array.from(files).forEach((file) => {
    const randomId = Math.floor(Math.random() * 100);
    list.splice(0, 0, { id: file.name + randomId, name: file.name });
  });
}
function handleRemove(id, list) {
  const index = list.findIndex(item => item.id === id);
  list.splice(index, 1);
}
function handleError() {
  EventBus.$emit('renderGlobalMessage', ({ message: 'At least one of the files you have added is invalid', icon: 'times', type: 'error' }));
}
</script>
